import { Dispatch, memo, SetStateAction, SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react"
import { AutocompleteInputChangeReason, Box } from "@mui/material"
import { AxiosInstance } from "axios"
import { debounce } from "lodash-es"
import { makeStyles } from "tss-react/mui"

import { ApiQueryAutocomplete } from "../../../../core/api/components/ApiQueryAutocomplete"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import {
    CompanyAdminUsersQuery,
    fetchCompaniesUsers,
} from "../../../../modules/company/admin/organization/users/usersApi"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../../modules/localization/components/AppLocalizationProvider"
import { TranslationKey } from "../../../../modules/localization/translations"
import { UserType } from "../../../../modules/login/loginTypes"

import { UserDetailsProps } from "./OrderOnBehalfFlow"
import {
    CreateOrderOnBehalfOfQuery,
    fetchOrderOnBehalfOfUsersForMerchant,
    fetchOrderOnBehalfOfUsersForServiceCenter,
    OrderOnBehalfOfUser,
    OrderOnBehalfOfUsersForMerchantResponse,
} from "./orderOnBehalfFlowApi"
import { OrderOnBehalfScope } from "./orderOnBehalfTypes"

const MIN_SEARCH_LENGTH = 3
const SEARCH_DEBOUNCE_MS = 300

const useStyles = makeStyles()((_theme) => ({
    input: {
        "& .MuiFormControl-root": {
            width: "100%",
        },
    },
}))

const transformData = (data: OrderOnBehalfOfUsersForMerchantResponse) => data?.users || []

type UserSelectorProps = Readonly<{
    scope: OrderOnBehalfScope
    userDetails: UserDetailsProps
    setUserDetails: Dispatch<SetStateAction<UserDetailsProps>>
    labelTranslationKey?: TranslationKey
}>

export const UserSelector = memo(
    ({
        scope,
        userDetails,
        setUserDetails,
        labelTranslationKey = "create-order-on-behalf-of-dialog-select-user-field",
    }: UserSelectorProps) => {
        const { l10n } = useAppLocalization()
        const { classes } = useStyles()

        // `inputValue` is what the field shows (MUI writes the full option label into it after a
        // selection), `searchText`/`debouncedSearchText` is what we actually query with. Keeping
        // them apart is what stops the option label from being sent as `searchText`.
        const [inputValue, setInputValue] = useState<string>("")
        const [searchText, setSearchText] = useState<string>("")
        const [debouncedSearchText, setDebouncedSearchText] = useState<string>("")
        const [selectedUser, setSelectedUser] = useState<OrderOnBehalfOfUser | null>(null)

        const debounceSearch = useMemo(
            () => debounce((value: string) => setDebouncedSearchText(value), SEARCH_DEBOUNCE_MS),
            [],
        )

        useEffect(() => {
            debounceSearch(searchText)
            return () => debounceSearch.cancel()
        }, [searchText, debounceSearch])

        const resetSearch = useCallback(() => {
            setInputValue("")
            setSearchText("")
            setDebouncedSearchText("")
        }, [])

        // The parent clears `selectedUserId` when the company changes or the form is reset.
        useEffect(() => {
            if (!userDetails.selectedUserId) {
                setSelectedUser(null)
                resetSearch()
            }
        }, [userDetails.selectedUserId, resetSearch])

        const { queryFn, queryName } = useMemo(() => {
            return {
                queryFn: async (
                    {
                        scope,
                        companyId,
                        companyGroupId,
                        search,
                    }: { scope: OrderOnBehalfScope; companyId?: string; companyGroupId?: string; search?: string },
                    axios: AxiosInstance,
                    signal?: AbortSignal,
                ) => {
                    if (scope.type === UserType.MERCHANT) {
                        const params: {
                            merchantId: number
                            companyId?: string
                            userGroupId?: string
                            searchText?: string
                        } = {
                            merchantId: scope.merchantId,
                        }

                        if (companyId) params.companyId = companyId
                        if (companyGroupId) params.userGroupId = companyGroupId
                        if (search) params.searchText = search

                        return fetchOrderOnBehalfOfUsersForMerchant(params, axios, signal)
                    }

                    if (scope.type === UserType.SERVICE_CENTER) {
                        if (!companyId) return { users: [] }

                        const params: {
                            serviceCenterId: number
                            companyId: string
                            userGroupId?: string
                            searchText?: string
                        } = {
                            serviceCenterId: scope.serviceCenterId,
                            companyId,
                            userGroupId: companyGroupId,
                        }

                        if (search) params.searchText = search

                        return fetchOrderOnBehalfOfUsersForServiceCenter(params, axios, signal)
                    }

                    return fetchCompaniesUsers({ companyId: scope.companyId, search }, axios, signal)
                },
                queryName:
                    scope.type === UserType.MERCHANT
                        ? CreateOrderOnBehalfOfQuery.USERS_MERCHANT
                        : scope.type === UserType.SERVICE_CENTER
                          ? CreateOrderOnBehalfOfQuery.COMPANIES_USERS_SERVICE_CENTER
                          : CompanyAdminUsersQuery.USERS_FOR_COMPANY,
            }
        }, [scope.type])

        const hasEnoughSearchText = debouncedSearchText.length >= MIN_SEARCH_LENGTH
        // Keep showing the spinner while the debounce is still pending, otherwise "no results"
        // flashes for 300ms on every keystroke.
        const isSearchPending = searchText !== debouncedSearchText && searchText.length >= MIN_SEARCH_LENGTH

        const {
            data: result = [],
            isError,
            isInitialLoading,
        } = useApiQuery(queryFn, {
            queryName,
            dependencies: {
                scope,
                search: debouncedSearchText,
                companyId: userDetails.companyId,
                companyGroupId: userDetails.companyGroupId,
            },
            select: transformData,
            enabled: hasEnoughSearchText,
        })

        // The query is disabled right after a selection, so `result` is empty while a user is
        // selected — keep the selected option in the list so MUI can match `value` against it.
        const options = useMemo(() => {
            if (!selectedUser) return result

            return [selectedUser, ...result.filter((user) => user.id !== selectedUser.id)]
        }, [result, selectedUser])

        const autocompleteKey = useMemo(() => {
            return `${userDetails.companyId || "none"}-${userDetails.companyGroupId || "none"}`
        }, [userDetails.companyId, userDetails.companyGroupId])

        const handleChange = (_event: SyntheticEvent, value: OrderOnBehalfOfUser | null) => {
            setSelectedUser(value)
            setSearchText("")
            setDebouncedSearchText("")
            setUserDetails((prev) => ({
                ...prev,
                selectedUserId: value?.id,
                selectedUserName: value?.displayName,
            }))
        }

        const handleInputChange = (
            _event: SyntheticEvent,
            newInputValue: string,
            reason: AutocompleteInputChangeReason,
        ) => {
            // "reset" / "clear" / "blur": MUI writes the selected option's label (or an empty
            // string) into the field itself — that is not something the user searched for.
            if (reason !== "input") {
                setInputValue(newInputValue)
                setSearchText("")
                return
            }

            // After a selection the field holds the whole displayName
            // ("#32656, test10, Jeppe Fjord (mail)"). Editing it means "I want somebody else", so
            // start a clean search instead of querying the backend with that entire label.
            if (selectedUser) {
                setSelectedUser(null)
                resetSearch()
                setUserDetails((prev) => ({ ...prev, selectedUserId: undefined, selectedUserName: undefined }))
                return
            }

            setInputValue(newInputValue)
            setSearchText(newInputValue)
        }

        return (
            <Box className={classes.input}>
                <LocalizedStrict id={labelTranslationKey} attrs={{ label: true, placeholder: true }}>
                    <ApiQueryAutocomplete
                        key={autocompleteKey}
                        isLoading={isInitialLoading || isSearchPending}
                        isError={isError}
                        options={options}
                        value={selectedUser}
                        inputValue={inputValue}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.displayName}
                        filterOptions={(x) => x}
                        onChange={handleChange}
                        onInputChange={handleInputChange}
                        label="Select user"
                        placeholder="Enter user name:"
                        noOptionsText={
                            hasEnoughSearchText
                                ? l10n.getString("create-order-on-behalf-of-dialog-select-user-field-no-results")
                                : ""
                        }
                        slotProps={{
                            textField: { className: classes.input },
                            errorSelect: { className: classes.input },
                        }}
                    />
                </LocalizedStrict>
            </Box>
        )
    },
)
